/* ==========================================================================
   Swello Cloud — interactions & motion
   No dependencies. Everything runs off a single requestAnimationFrame loop
   (Ticker) and calms down for visitors who prefer reduced motion.
   ========================================================================== */
(() => {
  'use strict';

  const S = window.Swello || { routes: {}, settings: {} };
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const rand = (a, b) => a + Math.random() * (b - a);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  const CLOUD_PATH = 'M64 172c-33 0-58-23-58-53 0-28 22-51 51-53 4-36 35-63 72-63 29 0 54 17 66 41 7-3 15-4 23-4 34 0 62 27 63 61 22 6 33 26 33 42 0 16-12 29-30 29H64z';
  const cloudSVG = `<svg viewBox="0 0 320 180" aria-hidden="true"><path fill="currentColor" d="${CLOUD_PATH}"/></svg>`;

  /* ---------- Ticker: one rAF loop for everything ---------- */
  const Ticker = {
    fns: new Set(),
    last: performance.now(),
    add(fn) { this.fns.add(fn); },
    remove(fn) { this.fns.delete(fn); },
    loop(now) {
      const dt = Math.min(0.05, (now - Ticker.last) / 1000);
      Ticker.last = now;
      Scroll.update();
      Ticker.fns.forEach((fn) => fn(dt, now));
      requestAnimationFrame(Ticker.loop);
    }
  };

  /* ---------- Scroll velocity ---------- */
  const Scroll = {
    y: window.scrollY, prev: window.scrollY, v: 0,
    update() {
      this.y = window.scrollY;
      this.v = lerp(this.v, this.y - this.prev, 0.18);
      this.prev = this.y;
    }
  };

  /* ---------- Pointer (normalised -1..1) ---------- */
  const Pointer = { x: 0, y: 0, cx: 0, cy: 0 };
  window.addEventListener('pointermove', (e) => {
    Pointer.cx = e.clientX; Pointer.cy = e.clientY;
    Pointer.x = (e.clientX / innerWidth) * 2 - 1;
    Pointer.y = (e.clientY / innerHeight) * 2 - 1;
  }, { passive: true });

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || S.moneyFormat || '${{amount}}';
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      n = (n / 100).toFixed(decimals);
      const [whole, frac] = n.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (frac ? dec + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------- Split text into animated characters ---------- */
  function splitText(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = '1';
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    let i = 0;
    const esc = (c) => c.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    el.setAttribute('aria-label', text);
    el.innerHTML = text.split(' ').map((word) =>
      `<span class="w" aria-hidden="true">${Array.from(word).map((c) => `<span class="ch" style="--ci:${i++}">${esc(c)}</span>`).join('')}</span>`
    ).join(' ');
  }

  /* ---------- Reveal on scroll ---------- */
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add('is-in');
      if (el.hasAttribute('data-stat')) countUp(el);
      revealIO.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  function countUp(el) {
    const target = parseInt(el.dataset.stat, 10) || 0;
    const out = $('[data-stat-num]', el);
    if (!out) return;
    if (reduce) { out.textContent = target; return; }
    const t0 = performance.now();
    const dur = 2200;
    const step = (now) => {
      const t = clamp((now - t0) / dur);
      const eased = 1 - Math.pow(2, -10 * t);
      out.textContent = Math.round(target * (t === 1 ? 1 : eased));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---------- Loader (first visit per session) ---------- */
  function Loader() {
    const el = $('[data-loader]');
    let fired = false;
    const ready = () => {
      if (fired) return;
      fired = true;
      document.documentElement.classList.add('is-ready');
      document.dispatchEvent(new CustomEvent('swello:ready'));
    };
    if (!el) { requestAnimationFrame(ready); return; }

    let seen = null;
    try { seen = sessionStorage.getItem('swello-intro'); } catch (e) { /* storage blocked */ }
    if (seen || reduce) { el.remove(); requestAnimationFrame(ready); return; }
    try { sessionStorage.setItem('swello-intro', '1'); } catch (e) { /* storage blocked */ }

    const t0 = performance.now();
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      setTimeout(() => {
        el.classList.add('is-done');
        setTimeout(ready, 380);
        setTimeout(() => el.remove(), 2000);
      }, Math.max(0, 1400 - (performance.now() - t0)));
    };
    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });
    setTimeout(finish, 3200);
  }

  /* ---------- Hero: cloud sky canvas ---------- */
  class CloudSky {
    constructor(canvas, hero) {
      this.canvas = canvas;
      this.hero = hero;
      this.ctx = canvas.getContext('2d');
      this.dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      this.mx = 0; this.my = 0;
      this.visible = true;
      this.sprites = Array.from({ length: 6 }, (_, i) => this.makeSprite(520, 260, i));
      this.resize();
      window.addEventListener('resize', () => this.resize());
      new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; }).observe(hero);
      this.tick = this.tick.bind(this);
      if (reduce) this.draw(0); else Ticker.add(this.tick);
    }

    makeSprite(w, h, seed) {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d');
      const n = 9 + (seed % 4) * 2;
      for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        const r = h * (0.17 + 0.2 * Math.sin(Math.PI * t)) * rand(0.8, 1.2);
        const x = w * (0.14 + 0.72 * t) + rand(-12, 12);
        const y = h * 0.66 - r * 0.45 + rand(-10, 10);
        const grd = g.createRadialGradient(x, y - r * 0.25, r * 0.1, x, y, r);
        grd.addColorStop(0, 'rgba(255,255,255,1)');
        grd.addColorStop(0.6, 'rgba(255,255,255,.75)');
        grd.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = grd;
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
      }
      g.globalCompositeOperation = 'source-atop';
      const shade = g.createLinearGradient(0, h * 0.35, 0, h);
      shade.addColorStop(0, 'rgba(255,255,255,0)');
      shade.addColorStop(1, 'rgba(140,166,200,.45)');
      g.fillStyle = shade;
      g.fillRect(0, 0, w, h);
      return c;
    }

    resize() {
      const { clientWidth: w, clientHeight: h } = this.hero;
      if (w === this.w && h === this.h) return;
      const widthChanged = w !== this.w;
      this.w = w; this.h = h;
      this.canvas.width = w * this.dpr;
      this.canvas.height = h * this.dpr;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      // Mobile browsers fire "resize" when the address bar shows/hides while scrolling.
      // Only build a new sky when the width really changes; otherwise keep every cloud where it is.
      if (widthChanged || !this.clouds) {
        const count = Math.round(clamp(w / 120, 7, 14));
        this.clouds = Array.from({ length: count }, (_, i) => {
          const depth = rand(0.25, 1);
          return {
            sprite: this.sprites[i % this.sprites.length],
            depth,
            x: rand(-0.2, 1.1) * w,
            yf: rand(0.02, 0.72),
            scale: lerp(0.35, 1.25, depth) * clamp(w / 1400, 0.6, 1.1),
            speed: lerp(4, 18, depth),
            alpha: lerp(0.45, 0.95, depth)
          };
        }).sort((a, b) => a.depth - b.depth);
      }
      if (reduce) this.draw(0);
    }

    tick(dt) {
      if (!this.visible) return;
      // Gentle mouse parallax on desktop only — touch scrolling must not move the clouds
      if (finePointer) {
        this.mx = lerp(this.mx, Pointer.x, 0.04);
        this.my = lerp(this.my, Pointer.y, 0.04);
      }
      this.draw(dt);
    }

    draw(dt) {
      const { ctx, w, h } = this;
      ctx.clearRect(0, 0, w, h);
      for (const c of this.clouds) {
        const cw = c.sprite.width * c.scale;
        const ch = c.sprite.height * c.scale;
        c.x += c.speed * dt;
        if (c.x > w + 40) { c.x = -cw - rand(0, 200); c.yf = rand(0.02, 0.72); }
        const px = c.x - this.mx * 30 * c.depth;
        const py = c.yf * h - this.my * 12 * c.depth;
        ctx.globalAlpha = c.alpha;
        ctx.drawImage(c.sprite, px, py, cw, ch);
      }
      ctx.globalAlpha = 1;
    }
  }

  /* ---------- Hero: parallax + intro ---------- */
  function Hero(hero) {
    const canvas = $('[data-cloud-sky]', hero);
    if (canvas) new CloudSky(canvas, hero);
    const splits = $$('[data-split]', hero);
    const go = () => {
      hero.classList.add('is-ready');
      splits.forEach((el) => el.classList.add('is-in'));
    };
    if (document.documentElement.classList.contains('is-ready')) go();
    else document.addEventListener('swello:ready', go, { once: true });

    if (reduce) return;
    const layers = $$('[data-depth]', hero).map((el) => ({ el, d: parseFloat(el.dataset.depth) || 1, x: 0, y: 0 }));
    Ticker.add(() => {
      const hp = clamp(Scroll.y / (hero.offsetHeight || 1));
      if (hp >= 1) return;
      hero.style.setProperty('--hp', hp.toFixed(4));
      if (!finePointer) return;
      for (const l of layers) {
        l.x = lerp(l.x, Pointer.x * 16 * l.d, 0.08);
        l.y = lerp(l.y, Pointer.y * 12 * l.d, 0.08);
        l.el.style.setProperty('--tx', `${l.x.toFixed(2)}px`);
        l.el.style.setProperty('--ty', `${l.y.toFixed(2)}px`);
      }
    });
  }

  /* ---------- Header ---------- */
  function Header() {
    const header = $('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    const menu = $('[data-menu]');
    const burger = $('[data-menu-open]', header);

    Ticker.add(() => {
      const y = Scroll.y;
      header.classList.toggle('is-scrolled', y > 24);
      if (menu && menu.classList.contains('is-open')) return;
      if (y > lastY + 6 && y > 400) header.classList.add('is-hidden');
      else if (y < lastY - 6 || y < 200) header.classList.remove('is-hidden');
      lastY = y;
    });

    const setMenu = (open) => {
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      burger.setAttribute('aria-expanded', String(open));
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) $('a, button', menu)?.focus({ preventScroll: true });
    };
    burger?.addEventListener('click', () => setMenu(true));
    $('[data-menu-close]')?.addEventListener('click', () => { setMenu(false); burger.focus(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && menu?.classList.contains('is-open')) setMenu(false); });
  }

  /* ---------- Announcement bar ---------- */
  function Announcement(el) {
    const msgs = $$('.announce__msg', el);
    if (msgs.length > 1 && !reduce) {
      let i = 0;
      setInterval(() => {
        const cur = msgs[i];
        cur.classList.remove('is-active');
        cur.classList.add('is-leaving');
        setTimeout(() => cur.classList.remove('is-leaving'), 700);
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
      }, 4200);
    }
    const end = el.dataset.countdown ? new Date(el.dataset.countdown) : null;
    const timer = $('[data-countdown-timer]', el);
    if (!end || isNaN(end) || !timer) return;
    const parts = { h: $('[data-cd="h"]', timer), m: $('[data-cd="m"]', timer), s: $('[data-cd="s"]', timer) };
    const pad = (n) => String(n).padStart(2, '0');
    const render = () => {
      const diff = end - Date.now();
      if (diff <= 0) { timer.hidden = true; return false; }
      timer.hidden = false;
      parts.h.textContent = pad(Math.floor(diff / 3.6e6));
      parts.m.textContent = pad(Math.floor(diff / 6e4) % 60);
      parts.s.textContent = pad(Math.floor(diff / 1e3) % 60);
      return true;
    };
    if (render()) { const id = setInterval(() => { if (!render()) clearInterval(id); }, 1000); }
  }

  /* ---------- Countdown bar (loops: counts down from N hours, then starts over) ---------- */
  function CountdownBar(el) {
    const copyBtn = $('[data-copy]', el);
    const copyText = async (text) => {
      try { await navigator.clipboard.writeText(text); return true; } catch (err) {
        // older browsers / blocked clipboard API: copy through a temporary field
        const field = Object.assign(document.createElement('textarea'), { value: text, readOnly: true });
        field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.append(field);
        field.select();
        const ok = document.execCommand('copy');
        field.remove();
        return ok;
      }
    };
    copyBtn?.addEventListener('click', async () => {
      if (!(await copyText(copyBtn.dataset.copy))) return;
      copyBtn.classList.add('is-copied');
      clearTimeout(copyBtn._t);
      copyBtn._t = setTimeout(() => copyBtn.classList.remove('is-copied'), 1600);
    });

    const parts = { h: $('[data-cbar="h"]', el), m: $('[data-cbar="m"]', el), s: $('[data-cbar="s"]', el) };
    if (!parts.h) return;
    const cycle = Math.max(1, Number(el.dataset.hours) || 8) * 3.6e6;

    // "visitor": the cycle starts on this shopper's first visit (remembered in this browser).
    // "synced": cycles are counted from a fixed point in time, so every shopper sees the same clock.
    let start = 0;
    if (el.dataset.mode !== 'synced') {
      const key = `swello-cbar-${el.dataset.key}`;
      try {
        start = Number(localStorage.getItem(key)) || 0;
        if (!start || start > Date.now()) { start = Date.now(); localStorage.setItem(key, String(start)); }
      } catch (err) { start = Date.now(); }
    }

    const pad = (n) => String(n).padStart(2, '0');
    const render = () => {
      const left = Math.ceil((cycle - ((Date.now() - start) % cycle)) / 1000);
      parts.h.textContent = pad(Math.floor(left / 3600));
      parts.m.textContent = pad(Math.floor(left / 60) % 60);
      parts.s.textContent = pad(left % 60);
    };
    render();
    const id = setInterval(() => { if (!el.isConnected) clearInterval(id); else render(); }, 1000);
  }

  /* ---------- Marquee (steady speed) ---------- */
  function Marquee(el) {
    const track = $('[data-marquee-track]', el);
    const group = $('[data-marquee-group]', el);
    if (!track || !group || !group.children.length) return;
    const dir = parseFloat(el.dataset.direction) || -1;
    const speed = parseFloat(el.dataset.speed) || 60;
    let groupW = 0;
    let x = 0;
    let visible = false;
    let paused = false;
    let builtFor = 0;   // window width the copies were made for

    const fill = () => {
      // Mobile browsers fire "resize" when the address bar shows/hides while scrolling.
      // Only rebuild when the width really changes (e.g. turning the phone), and keep the strip where it is:
      // rebuilding on every scroll made it jump back to the start and reload its videos.
      if (innerWidth === builtFor) return;
      const first = !builtFor;
      builtFor = innerWidth;
      $$('[data-marquee-clone]', track).forEach((n) => n.remove());
      groupW = group.offsetWidth;
      const copies = Math.max(1, Math.ceil((innerWidth * 1.5) / Math.max(groupW, 1)));
      const clones = [];
      for (let i = 0; i < copies; i++) {
        const clone = group.cloneNode(true);
        clone.setAttribute('data-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
        clones.push(clone);
      }
      if (first) {
        x = dir > 0 ? -groupW : 0;
      } else {
        x = ((x % groupW) - groupW) % groupW;   // same spot within the loop
        clones.forEach((c) => Videos(c));       // new copies' videos play while visible, like the originals
      }
    };
    fill();
    window.addEventListener('resize', fill);
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(el);
    if (el.hasAttribute('data-pause-hover')) {
      el.addEventListener('pointerenter', () => { paused = true; });
      el.addEventListener('pointerleave', () => { paused = false; });
    }
    if (reduce) return;

    Ticker.add((dt) => {
      if (!visible || !groupW) return;
      // Constant speed — scrolling the page never speeds the ticker up or slows it down
      x += dir * (paused ? speed * 0.15 : speed) * dt;
      if (dir < 0 && x <= -groupW) x += groupW;
      if (dir > 0 && x >= 0) x -= groupW;
      track.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
    });
  }

  /* ---------- 2X stuffed scroll story ---------- */
  /* Draws a side view of a quilted comforter lying on a mattress (SVG). The Swello one
     inflates from 1X to 2X height as you scroll while fill fibers pour in. */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  let comforterUid = 0;

  function Comforter(svg, opts = {}) {
    const uid = `cf${++comforterUid}`;
    const BASE = 176;              // top of the mattress (comforter rests here)
    const BED_L = 30, BED_R = 586; // mattress ends
    const X0 = 118, X1 = BED_R;    // comforter: from the pillow to the foot of the bed
    const POCKETS = 5;
    const H1 = 26;                 // pocket height at 1X
    const el = (tag, attrs) => { const n = document.createElementNS(SVG_NS, tag); for (const k in attrs) n.setAttribute(k, attrs[k]); return n; };

    svg.innerHTML = '';
    const defs = el('defs', {});
    const shell = el('linearGradient', { id: `${uid}-shell`, x1: 0, y1: 0, x2: 0, y2: 1 });
    shell.append(el('stop', { offset: '0', 'stop-color': '#FFFFFF' }), el('stop', { offset: '.6', 'stop-color': '#F1F5F9' }), el('stop', { offset: '1', 'stop-color': '#CFDAE7' }));
    const clip = el('clipPath', { id: `${uid}-clip` });
    const clipPath = el('path', {});
    clip.append(clipPath);
    defs.append(shell, clip);

    const wood = 'rgba(255,255,255,.14)';
    const bed = el('g', {});
    bed.append(
      el('rect', { x: 8, y: 112, width: 18, height: 112, rx: 6, fill: wood }),                       // headboard
      el('rect', { x: BED_L - 4, y: BASE, width: BED_R - BED_L + 8, height: 30, rx: 8, fill: 'rgba(255,255,255,.10)', stroke: 'rgba(255,255,255,.18)' }), // mattress
      el('rect', { x: BED_L, y: BASE + 30, width: BED_R - BED_L, height: 8, rx: 3, fill: wood }),    // bed frame
      el('rect', { x: BED_L + 6, y: BASE + 38, width: 8, height: 12, rx: 2, fill: wood }),           // legs
      el('rect', { x: BED_R - 14, y: BASE + 38, width: 8, height: 12, rx: 2, fill: wood }),
      el('ellipse', { cx: 76, cy: BASE - 13, rx: 46, ry: 14, fill: '#E9EFF6', stroke: 'rgba(255,255,255,.8)', 'stroke-width': 1.2 }) // pillow
    );
    const body = el('path', { fill: `url(#${uid}-shell)`, stroke: 'rgba(255,255,255,.9)', 'stroke-width': 1.5, 'stroke-linejoin': 'round' });

    const fillGroup = el('g', { 'clip-path': `url(#${uid}-clip)` });
    const dots = [];
    for (let i = 0; i < 160; i++) {
      const d = el('circle', { cx: rand(X0, X1 + 6), cy: rand(BASE - 60, BASE + 26), r: rand(2.5, 6), fill: '#FFFFFF' });
      d._t = i / 160;
      dots.push(d);
      fillGroup.append(d);
    }
    const stitches = el('g', { stroke: 'rgba(29,39,66,.25)', 'stroke-width': 1.5, 'stroke-dasharray': '3 4', 'stroke-linecap': 'round' });
    const stitchLines = [];
    for (let k = 1; k < POCKETS; k++) { const l = el('line', {}); stitchLines.push(l); stitches.append(l); }
    const sheen = el('path', { fill: 'none', stroke: 'rgba(255,255,255,.95)', 'stroke-width': 3, 'stroke-linecap': 'round' });
    svg.append(defs, bed, body, fillGroup, stitches, sheen);
    if (opts.reference) {
      // dashed marker at the height of a standard comforter, for comparison
      const refY = BASE - H1;
      const ref = el('g', {});
      ref.append(
        el('line', { x1: X0 - 6, x2: X1 + 12, y1: refY, y2: refY, stroke: 'rgba(255,255,255,.55)', 'stroke-width': 1.2, 'stroke-dasharray': '5 5' }),
        Object.assign(el('text', { x: X1 + 16, y: refY + 3.5, 'text-anchor': 'start', fill: 'rgba(255,255,255,.6)', 'font-size': 10, 'font-weight': 700, 'font-family': 'inherit' }), { textContent: '1X' })
      );
      svg.append(ref);
    }

    const w = (X1 - X0) / POCKETS;
    return {
      topAt(p) { return BASE - H1 * (1 + p); },
      update(p) {
        const H = H1 * (1 + p);
        const pinch = 6 + 4 * p;                 // thickness at the stitch lines
        const peak = BASE - H * 1.33;            // cubic control height -> visual peak ~ H
        const drapeX = X1 + 4 + 3 * p;           // comforter hangs over the foot of the bed
        let d = `M ${X0} ${BASE}`;
        let top = '';
        for (let k = 0; k < POCKETS; k++) {
          const xa = X0 + k * w;
          const xb = xa + w;
          const last = k === POCKETS - 1;
          const yb = last ? BASE - H * 0.55 : BASE - pinch;
          const c1 = k === 0 ? `${xa} ${peak}` : `${xa + w * 0.18} ${peak}`;
          const c2 = last ? `${xb + 2} ${peak}` : `${xb - w * 0.18} ${peak}`;
          d += ` C ${c1} ${c2} ${xb} ${yb}`;
          top += ` M ${xa + w * 0.28} ${BASE - H * 0.92} Q ${xa + w * 0.5} ${BASE - H * 1.01} ${xa + w * 0.72} ${BASE - H * 0.92}`;
        }
        // drape down over the end of the mattress, then back along the mattress top
        d += ` C ${drapeX + 2} ${BASE - H * 0.2} ${drapeX} ${BASE + 18} ${drapeX - 2} ${BASE + 26}`;
        d += ` L ${X1 - 6} ${BASE + 26} L ${X1 - 6} ${BASE} L ${X0} ${BASE} Z`;
        body.setAttribute('d', d);
        clipPath.setAttribute('d', d);
        sheen.setAttribute('d', top.trim());
        sheen.setAttribute('opacity', (0.35 + 0.45 * p).toFixed(2));
        stitchLines.forEach((l, i) => {
          const x = X0 + (i + 1) * w;
          l.setAttribute('x1', x); l.setAttribute('x2', x);
          l.setAttribute('y1', BASE - pinch + 1); l.setAttribute('y2', BASE - 1);
        });
        const visible = 0.3 + 0.7 * p;           // fill density
        dots.forEach((dot) => dot.setAttribute('opacity', dot._t < visible ? 0.55 : 0));
      }
    };
  }

  function Stuffed(el) {
    const count = $('[data-stuffed-count]', el);
    const tag = $('[data-cf-tag]', el);
    const steps = $$('[data-stuffed-step]', el);
    const canvas = $('[data-fibers]', el);
    const swelloSvg = $('[data-comforter="swello"]', el);
    const standardSvg = $('[data-comforter="standard"]', el);
    if (!swelloSvg) return;
    const swello = Comforter(swelloSvg, { reference: true });
    if (standardSvg) Comforter(standardSvg).update(0);
    swello.update(0);

    const ctx = canvas?.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let fibers = [];
    let lastFill = -1;
    let full = false;
    let shown = -1;

    const size = () => {
      if (!canvas) return;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    window.addEventListener('resize', size);

    // y (in canvas px) of the comforter's top surface for the current fill
    const floorFor = (fill) => {
      const svgBox = swelloSvg.getBoundingClientRect();
      const cBox = canvas.getBoundingClientRect();
      const vb = swelloSvg.viewBox.baseVal;
      return svgBox.top - cBox.top + ((swello.topAt(fill) - vb.y) / vb.height) * svgBox.height;
    };

    Ticker.add((dt) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -50 || rect.top > innerHeight + 50) return;
      const total = el.offsetHeight - innerHeight;
      const raw = clamp(-rect.top / Math.max(total, 1));
      const t = clamp((raw - 0.18) / 0.58);
      const fill = reduce ? t : 1 - Math.pow(1 - t, 3);
      el.style.setProperty('--p', fill.toFixed(4));
      if (Math.abs(fill - lastFill) > 0.0005) {
        swello.update(fill);
        const label = `${(1 + fill).toFixed(1)}`;
        if (count) count.textContent = label;
        if (tag) tag.textContent = `${label}X`;
      }

      const idx = Math.min(steps.length - 1, Math.floor(raw * steps.length * 1.05));
      if (idx !== shown) {
        steps.forEach((s, i) => s.classList.toggle('is-active', i === idx));
        shown = idx;
      }
      if (fill > 0.995 && !full) { full = true; el.classList.add('is-full'); }
      if (fill < 0.95 && full) { full = false; el.classList.remove('is-full'); }

      if (!ctx || reduce) { lastFill = fill; return; }
      const delta = fill - Math.max(lastFill, 0);
      lastFill = fill;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (delta > 0) {
        const n = Math.min(40, Math.ceil(delta * 900));
        for (let i = 0; i < n; i++) {
          fibers.push({ x: rand(0.06, 0.94) * w, y: rand(-30, 0), vy: rand(40, 120), sway: rand(0, Math.PI * 2), r: rand(2.5, 6), curl: rand(6, 14), a: rand(0.6, 1) });
        }
      }
      const floor = floorFor(fill) + 6;
      ctx.clearRect(0, 0, w, h);
      fibers = fibers.filter((f) => {
        f.vy += 260 * dt;
        f.y += f.vy * dt;
        f.sway += dt * 4;
        if (f.y > floor) return false;
        const fx = f.x + Math.sin(f.sway) * f.curl;
        ctx.globalAlpha = f.a;
        ctx.fillStyle = '#fff';
        // three overlapping circles, each its own sub-path so there are no see-through holes
        ctx.beginPath();
        ctx.moveTo(fx + f.r, f.y);
        ctx.arc(fx, f.y, f.r, 0, Math.PI * 2);
        ctx.moveTo(fx + f.r * 1.6, f.y - f.r * 0.5);
        ctx.arc(fx + f.r * 0.9, f.y - f.r * 0.5, f.r * 0.7, 0, Math.PI * 2);
        ctx.moveTo(fx - f.r * 0.3, f.y - f.r * 0.3);
        ctx.arc(fx - f.r * 0.9, f.y - f.r * 0.3, f.r * 0.6, 0, Math.PI * 2);
        ctx.fill('nonzero');
        return true;
      });
      ctx.globalAlpha = 1;
    });
  }

  /* ---------- Horizontal pinned scroll ---------- */
  function HScroll(el) {
    const track = $('[data-hscroll-track]', el);
    if (!track) return;
    const mq = matchMedia('(min-width: 768px)');
    let dist = 0;
    let x = 0;

    const setup = () => {
      if (!mq.matches) { el.style.height = ''; track.style.transform = ''; return; }
      dist = Math.max(0, track.scrollWidth - innerWidth);
      el.style.height = `${dist + innerHeight}px`;
    };
    setup();
    window.addEventListener('resize', setup);
    window.addEventListener('load', setup);
    mq.addEventListener?.('change', setup);

    Ticker.add(() => {
      if (!mq.matches) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const p = clamp(-rect.top / Math.max(el.offsetHeight - innerHeight, 1));
      const target = -p * dist;
      x = reduce ? target : lerp(x, target, 0.12);
      track.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
      el.style.setProperty('--hp', p.toFixed(4));
    });
  }

  /* ---------- Squish the cloud (spring physics) ---------- */
  function Squish(section) {
    const btn = $('[data-squish-target]', section);
    const body = $('.squish__body', btn);
    const shadow = $('[data-squish-shadow]', section);
    const counter = $('[data-squish-count]', section);
    const zzz = $('[data-squish-zzz]', section);
    if (!btn || !body) return;

    let total = 0;
    try { total = parseInt(localStorage.getItem('swello-squish'), 10) || 0; } catch (e) { /* storage blocked */ }
    if (counter) counter.textContent = total;

    const s = { sx: 1, sy: 1, tx: 0, rot: 0, vsx: 0, vsy: 0, vtx: 0, vrot: 0 };
    const target = { sx: 1, sy: 1, tx: 0, rot: 0 };
    let pressed = false;
    let pressT = 0;
    let offset = 0;
    let running = false;

    const apply = () => {
      body.style.setProperty('--sx', s.sx.toFixed(4));
      body.style.setProperty('--sy', s.sy.toFixed(4));
      body.style.setProperty('--tx', `${s.tx.toFixed(2)}px`);
      body.style.setProperty('--rot', `${s.rot.toFixed(2)}deg`);
      shadow?.style.setProperty('--ssx', s.sx.toFixed(3));
    };

    const step = (dt) => {
      if (pressed) {
        pressT = Math.min(1, pressT + dt * 2.2);
        target.sx = 1 + 0.22 * pressT;
        target.sy = 1 - 0.3 * pressT;
        target.tx = offset * 30;
        target.rot = offset * -5;
      }
      const k = pressed ? 0.16 : 0.1;
      const damp = pressed ? 0.7 : 0.82;
      for (const key of ['sx', 'sy', 'tx', 'rot']) {
        const v = `v${key}`;
        s[v] = (s[v] + (target[key] - s[key]) * k) * damp;
        s[key] += s[v];
      }
      apply();
      const settled = !pressed && ['sx', 'sy', 'tx', 'rot'].every((key) => Math.abs(target[key] - s[key]) < 0.001 && Math.abs(s[`v${key}`]) < 0.001);
      if (settled) { Ticker.remove(step); running = false; }
    };
    const run = () => { if (!running) { running = true; Ticker.add(step); } };

    const press = (clientX) => {
      pressed = true;
      pressT = 0;
      section.classList.add('is-touched');
      btn.classList.add('is-pressed');
      if (clientX != null) {
        const r = btn.getBoundingClientRect();
        offset = clamp(((clientX - r.left) / r.width) * 2 - 1, -1, 1);
      }
      run();
    };
    const release = () => {
      if (!pressed) return;
      pressed = false;
      btn.classList.remove('is-pressed');
      Object.assign(target, { sx: 1, sy: 1, tx: 0, rot: 0 });
      s.vsy += 0.06 + pressT * 0.12;
      s.vsx -= 0.04 + pressT * 0.08;
      total += 1;
      if (counter) {
        counter.textContent = total;
        counter.animate?.([{ transform: 'scale(1.5)' }, { transform: 'scale(1)' }], { duration: 500, easing: 'cubic-bezier(.34,1.56,.64,1)' });
      }
      try { localStorage.setItem('swello-squish', total); } catch (e) { /* storage blocked */ }
      spawnZ();
      const r = btn.getBoundingClientRect();
      poof(r.left + r.width / 2, r.top + r.height * 0.55, 8, 0.7);
      run();
    };

    const spawnZ = () => {
      if (!zzz || reduce) return;
      for (let i = 0; i < 3; i++) {
        const z = document.createElement('span');
        z.className = 'squish__z';
        z.textContent = 'z';
        z.style.left = `${62 + i * 4}%`;
        z.style.top = '30%';
        z.style.setProperty('--zs', `${1.1 + i * 0.5}rem`);
        zzz.appendChild(z);
        z.animate([
          { transform: 'translate(0,0) rotate(0deg)', opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: `translate(${40 + i * 25}px, ${-90 - i * 40}px) rotate(${rand(-20, 20)}deg)`, opacity: 0 }
        ], { duration: 1400 + i * 200, delay: i * 140, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' }).onfinish = () => z.remove();
      }
    };

    btn.addEventListener('pointerdown', (e) => { btn.setPointerCapture?.(e.pointerId); press(e.clientX); });
    btn.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      const r = btn.getBoundingClientRect();
      offset = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1);
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((ev) => btn.addEventListener(ev, release));
    btn.addEventListener('keydown', (e) => {
      if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); offset = 0; press(); }
    });
    btn.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') release(); });
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /* ---------- Night sky stars + moon parallax ---------- */
  function Night(el) {
    const stars = $('[data-stars]', el);
    if (stars && !stars.childElementCount) {
      const frag = document.createDocumentFragment();
      const n = Math.round(clamp(innerWidth / 9, 60, 160));
      for (let i = 0; i < n; i++) {
        const s = document.createElement('span');
        s.className = 'night__star';
        s.style.cssText = `left:${rand(0, 100)}%;top:${rand(0, 100)}%;--s:${rand(1, 3).toFixed(1)}px;--t:${rand(2, 5).toFixed(1)}s;--dl:${rand(-5, 0).toFixed(1)}s`;
        frag.appendChild(s);
      }
      stars.appendChild(frag);
    }
    if (reduce) return;
    Ticker.add(() => {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) return;
      el.style.setProperty('--np', clamp(1 - r.top / innerHeight, 0, 2).toFixed(4));
    });
  }

  /* ---------- Cloud poof particles ---------- */
  function poof(x, y, n = 14, power = 1) {
    if (reduce || S.settings.poof === false) return;
    for (let i = 0; i < n; i++) {
      const el = document.createElement('div');
      el.className = 'poof';
      el.innerHTML = cloudSVG;
      el.style.setProperty('--s', `${rand(18, 48) * power}px`);
      document.body.appendChild(el);
      const a = (Math.PI * 2 * i) / n + rand(-0.3, 0.3);
      const d = rand(60, 170) * power;
      const dx = Math.cos(a) * d;
      const dy = Math.sin(a) * d - 30;
      el.animate([
        { transform: `translate(${x}px, ${y}px) translate(-50%, -50%) scale(.2)`, opacity: 1 },
        { transform: `translate(${x + dx}px, ${y + dy}px) translate(-50%, -50%) scale(1) rotate(${rand(-40, 40)}deg)`, opacity: 0 }
      ], { duration: rand(800, 1300), easing: 'cubic-bezier(.16,1,.3,1)' }).onfinish = () => el.remove();
    }
  }

  /* ---------- Cart (drawer + Section Rendering API) ---------- */
  const Cart = {
    get drawer() { return $('[data-cart-drawer]'); },
    lastFocus: null,

    open() {
      const d = this.drawer;
      if (!d) return;
      this.lastFocus = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this.fit();
      setTimeout(() => $('.drawer__panel', d)?.focus({ preventScroll: true }), 50);
    },

    close() {
      const d = this.drawer;
      if (!d || !d.classList.contains('is-open')) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      this.lastFocus?.focus?.({ preventScroll: true });
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-inner]');
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      if (fresh) this.setCount(parseInt(fresh.dataset.count, 10) || 0);
      this.mountExpress();
      this.syncProtection();
      this.syncRate();
      this.reserve();
      this.fit();
    },

    /* Express checkout buttons (Shop Pay, PayPal, Google Pay…) arrive in a <template> (sections/cart-drawer).
       On iPhone/iPad Google Pay is left out of Shopify's wallet list before the buttons are added: Shopify
       reads that list only once, and hiding the button afterwards would leave an empty slot. */
    mountExpress() {
      const ios = document.documentElement.classList.contains('is-ios');
      $$('[data-express]').forEach((box) => {
        const tpl = $('template', box);
        if (!tpl || box.dataset.mounted) return;
        const frag = tpl.content.cloneNode(true);
        if (ios) {
          frag.querySelectorAll('[wallet-configs]').forEach((el) => {
            try {
              const wallets = JSON.parse(el.getAttribute('wallet-configs')).filter((w) => w.name !== 'google_pay');
              el.setAttribute('wallet-configs', JSON.stringify(wallets));
            } catch (e) { /* unknown format: keep Shopify's list (theme.css still hides the button) */ }
          });
          frag.querySelectorAll('shopify-google-pay-button').forEach((b) => b.remove());
        }
        box.dataset.mounted = '1';
        box.append(frag);
      });
    },

    /* Free shipping is set in the store's currency, and Liquid can't convert currencies. For shoppers
       paying in another currency, keep Shopify's exchange rate on the cart (hidden attribute _fx_rate)
       so the drawer and cart page can convert it (snippets/free-shipping-threshold). Only when the cart
       has items, at most once per rate per page. */
    syncRate() {
      const cur = window.Shopify && window.Shopify.currency;
      const inner = $('[data-cart-drawer-inner]');
      if (!cur || !cur.rate || !inner || !S.shopCurrency || cur.active === S.shopCurrency) return null;
      const rate = String(cur.rate);
      if (inner.dataset.fxRate === rate || this.rateTried === rate || !(parseInt(inner.dataset.count, 10) > 0)) return null;
      this.rateTried = rate;
      return this.request(`${S.routes.cart}/update.js`, { attributes: { _fx_rate: rate } }).catch(() => null);
    },

    /* No scrolling: the extra "Complete your cloud" suggestion only shows when it fits on screen */
    fit() {
      const body = $('.drawer__body');
      const ups = body && $('.drawer__upsells', body);
      if (!body || !ups) return;
      ups.hidden = false;
      if (body.scrollHeight > body.clientHeight + 1) ups.hidden = true;
    },

    /* "Reserved for mm:ss": one end time per visit (same on every page), starts over when it reaches 0 */
    reserve() {
      clearInterval(this._reserve);
      const el = $('[data-reserve]');
      const key = 'swello-reserve-end';
      if (!el) {
        const inner = $('[data-cart-drawer-inner]');
        if (inner && inner.dataset.count === '0') { try { localStorage.removeItem(key); } catch (e) { /* storage blocked */ } }
        return;
      }
      const clock = $('[data-reserve-clock]', el);
      const len = (parseInt(el.dataset.minutes, 10) || 10) * 60000;
      let end = 0;
      try { end = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) { /* storage blocked */ }
      const restart = () => { end = Date.now() + len; try { localStorage.setItem(key, String(end)); } catch (e) { /* storage blocked */ } };
      if (!end || end <= Date.now() || end - Date.now() > len) restart();
      const tick = () => {
        let left = end - Date.now();
        if (left <= 0) { restart(); left = len; }
        const s = Math.ceil(left / 1000);
        if (clock) clock.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
      };
      tick();
      this._reserve = setInterval(tick, 1000);
    },

    setCount(n, bump = true) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.classList.toggle('is-empty', n === 0);
        if (!bump) return;
        el.classList.remove('bump');
        void el.offsetWidth;
        el.classList.add('bump');
      });
    },

    async request(url, body) {
      const inner = $('[data-cart-drawer-inner]');
      inner?.classList.add('is-busy');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign(body, { sections: ['cart-drawer'], sections_url: window.location.pathname }))
        });
        const data = await res.json();
        if (data.sections) this.render(data.sections);
        else if (data.status) throw new Error(data.description || data.message);
        else window.location.reload();
        return data;
      } finally {
        $('[data-cart-drawer-inner]')?.classList.remove('is-busy');
      }
    },

    change(line, quantity) {
      return this.request(`${S.routes.cartChange}.js`, { line, quantity }).catch(() => { window.location.href = S.routes.cart; });
    },

    add(items) {
      return this.request(`${S.routes.cartAdd}.js`, { items });
    },

    /* Put a discount code on the cart itself, so the drawer shows the lower price right away
       (and checkout gets it too). Codes already on the cart are kept. */
    async applyCode(code) {
      const cart = await (await fetch(`${S.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
      const codes = (cart.discount_codes || []).map((c) => c.code).filter((c) => c.toLowerCase() !== code.toLowerCase());
      return this.request(`${S.routes.cart}/update.js`, { discount: codes.concat(code).join(',') });
    },

    /* Re-render the drawer from the server (e.g. after an app added items) */
    async refresh() {
      try {
        const res = await fetch(`${S.routes.root.replace(/\/$/, '')}/?sections=cart-drawer`);
        this.render(await res.json());
      } catch (e) { /* keep current drawer */ }
    },

    /* Shipping protection: keep it in sync with the cart contents and the shopper's choice */
    syncProtection() {
      const inner = $('[data-cart-drawer-inner]');
      if (!inner || this.syncing || !inner.dataset.protectionVariant) return;
      const line = parseInt(inner.dataset.protectionLine, 10) || 0;
      const count = parseInt(inner.dataset.count, 10) || 0;
      let pref = null;
      try { pref = sessionStorage.getItem('swello-protection'); } catch (e) { /* storage blocked */ }
      let job = null;
      if (count === 0 && line > 0) job = () => this.change(line, 0);
      else if (count > 0 && line === 0 && inner.dataset.protectionAuto === 'true' && pref !== 'off') {
        job = () => this.add([{ id: Number(inner.dataset.protectionVariant), quantity: 1 }]);
      }
      if (!job) return;
      this.syncing = true;
      Promise.resolve(job()).catch(() => {}).finally(() => { this.syncing = false; });
    },

    init() {
      const inner = $('[data-cart-drawer-inner]');
      if (inner) this.setCount(parseInt(inner.dataset.count, 10) || 0, false);
      this.mountExpress();
      this.reserve();
      // cart page: its free-shipping line is rendered by the server, so reload once the rate is saved
      const rateJob = this.syncRate();
      if (rateJob && document.body.classList.contains('template-cart')) {
        // reload only once the cart really has the rate, so a failed save can never cause a reload loop
        rateJob.then(() => { if ($('[data-cart-drawer-inner]')?.dataset.fxRate === String(window.Shopify.currency.rate)) window.location.reload(); });
      }

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener && this.drawer && !document.body.classList.contains('template-cart')) {
          e.preventDefault();
          this.open();
          this.refresh();
          return;
        }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
        if (e.target.closest('[data-cart-close-link]') && window.location.pathname === new URL(e.target.closest('a').href).pathname) { this.close(); return; }

        // Cart page +/− : update in place, then refresh the page (the link itself is the no-JS fallback)
        const pageQty = e.target.closest('.cart-page a.qty__btn');
        if (pageQty) {
          e.preventDefault();
          const u = new URL(pageQty.href);
          fetch(`${S.routes.cartChange}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(u.searchParams.get('line')), quantity: Math.max(0, Number(u.searchParams.get('quantity'))) })
          }).finally(() => window.location.reload());
          return;
        }

        const qtyBtn = e.target.closest('[data-line-qty]');
        if (qtyBtn && qtyBtn.closest('[data-cart-drawer]')) {
          const line = parseInt(qtyBtn.closest('[data-line]').dataset.line, 10);
          this.change(line, Math.max(0, parseInt(qtyBtn.dataset.lineQty, 10)));
          return;
        }

        const upsellBtn = e.target.closest('[data-upsell-add]');
        if (upsellBtn) {
          const card = upsellBtn.closest('[data-upsell]');
          const select = $('[data-upsell-select]', card);
          const id = Number(select ? select.value : upsellBtn.dataset.variantId);
          upsellBtn.classList.add('is-loading');
          this.add([{ id, quantity: 1 }]).catch((err) => {
            upsellBtn.classList.remove('is-loading');
            alert(err.message || 'Could not add this item.');
          });
        }
      });

      document.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-upsell-select]');
        if (sel) {
          const price = $('[data-upsell-price]', sel.closest('[data-upsell]'));
          const opt = sel.selectedOptions[0];
          if (price && opt?.dataset.price) price.textContent = opt.dataset.price;
          return;
        }
        const toggle = e.target.closest('[data-protection-toggle]');
        if (toggle) {
          const box = $('[data-cart-drawer-inner]');
          try { sessionStorage.setItem('swello-protection', toggle.checked ? 'on' : 'off'); } catch (err) { /* storage blocked */ }
          if (toggle.checked) this.add([{ id: Number(box.dataset.protectionVariant), quantity: 1 }]).catch(() => { toggle.checked = false; });
          else {
            const line = parseInt(box.dataset.protectionLine, 10);
            if (line) this.change(line, 0);
          }
        }
      });

      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      window.addEventListener('resize', () => { if (this.drawer?.classList.contains('is-open')) this.fit(); }, { passive: true });
    }
  };

  /* ---------- Buy box: gallery, variants, stock, delivery, tabs, add to cart ---------- */
  function BuyBox(root) {
    const json = $('[data-variants]', root);
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    let inventory = {};
    try { inventory = JSON.parse($('[data-inventory]', root).textContent); } catch (e) { /* no inventory data */ }
    const form = $('[data-product-form]', root);
    const idInput = form && $('[data-variant-id]', form);
    const fieldsets = $$('[data-option-index]', root);
    const addBtn = form && $('[data-add-button]', form);
    const addLabel = form && $('[data-add-label]', form);
    const addText = addLabel ? addLabel.textContent : 'Add to cart';
    const errorEl = form && $('[data-form-error]', form);
    const priceEl = $('[data-price]', root);
    const isProductPage = document.body.classList.contains('template-product') && root.closest('.product-main');
    const sticky = isProductPage ? $('[data-sticky-buy]') : null;

    /* gallery: native swipe track, thumbs, arrows, dots */
    const track = $('[data-gallery-track]', root);
    const slides = track ? $$('.gallery__slide', track) : [];
    const thumbs = $$('[data-thumb]', root);
    const dots = $$('.gallery__dot', root);
    let current = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

    const setActive = (i) => {
      current = i;
      slides.forEach((s, n) => {
        s.classList.toggle('is-active', n === i);
        const v = $('video', s);
        if (v) { if (n === i) v.play?.().catch(() => {}); else v.pause?.(); }
      });
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      // keep the active thumbnail in view when the thumbnail row scrolls (mobile)
      const thumb = thumbs[i];
      const row = thumb && thumb.parentElement;
      if (row && row.scrollWidth > row.clientWidth) {
        row.scrollTo({ left: thumb.offsetLeft - row.clientWidth / 2 + thumb.clientWidth / 2, behavior: reduce ? 'instant' : 'smooth' });
      }
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    };
    const goTo = (i, smooth = true) => {
      if (!track || !slides[i]) return;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'instant' });
      setActive(i);
    };
    const showMedia = (id) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) goTo(i);
    };
    if (track) {
      if (current > 0) requestAnimationFrame(() => goTo(current, false));
      let t;
      track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (i !== current) setActive(i);
        }, 80);
      }, { passive: true });
    }
    thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));
    $('[data-gallery-prev]', root)?.addEventListener('click', () => goTo((current - 1 + slides.length) % slides.length));
    $('[data-gallery-next]', root)?.addEventListener('click', () => goTo((current + 1) % slides.length));

    /* zoom + size guide dialogs */
    const lightbox = $('[data-lightbox]', root);
    $$('[data-zoom]', root).forEach((b) => b.addEventListener('click', () => {
      if (!lightbox?.showModal) return;
      const img = $('[data-lightbox-img]', lightbox);
      img.src = b.dataset.zoom;
      img.alt = $('img', b)?.alt || '';
      lightbox.showModal();
    }));
    $('[data-size-guide-open]', root)?.addEventListener('click', () => $('[data-size-guide]', root)?.showModal?.());
    $$('dialog', root).forEach((d) => {
      d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-modal-close]')) d.close(); });
    });

    /* tabs */
    $$('[data-tabs]', root).forEach((tabs) => {
      const btns = $$('[data-tab]', tabs);
      btns.forEach((btn) => btn.addEventListener('click', () => {
        btns.forEach((b) => { const on = b === btn; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', String(on)); });
        $$('[data-panel]', tabs).forEach((p) => { p.hidden = p.dataset.panel !== btn.dataset.tab; });
      }));
    });

    /* delivery estimate (business days, visitor's local time) */
    $$('[data-delivery]', root).forEach((el) => {
      const min = parseInt(el.dataset.min, 10) || 5;
      const max = Math.max(min, parseInt(el.dataset.max, 10) || min);
      const processing = Math.min(min, parseInt(el.dataset.processing, 10) || 0);
      const cutoff = parseInt(el.dataset.cutoff, 10);
      const isBiz = (d) => d.getDay() !== 0 && d.getDay() !== 6;
      const calendar = el.dataset.calendar === 'true';   // count weekends as delivery days
      const addBiz = (d, n) => { const x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); if (isBiz(x)) n--; } return x; };
      const addDays = (d, n) => (calendar ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + n) : addBiz(d, n));
      const day = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      const short = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const range = (a, b) => {
        if (a.toDateString() === b.toDateString()) return short(a);   // fastest = slowest: one date
        return a.getMonth() === b.getMonth() ? `${short(a)} – ${b.getDate()}` : `${short(a)} – ${short(b)}`;
      };
      const set = (sel, text) => { const n = $(sel, el); if (n) n.textContent = text; };
      const render = () => {
        const now = new Date();
        // Fixed ship day: ships N days after the shopper's today (0 = today, 1 = tomorrow…); delivery counted from today
        const fixedShip = el.dataset.shipToday === 'true';
        const beforeCutoff = isBiz(now) && now.getHours() < cutoff;
        const start = fixedShip || beforeCutoff ? now : addBiz(now, 1);
        const ship = fixedShip ? addDays(now, parseInt(el.dataset.shipDays, 10) || 0) : addBiz(start, processing);
        const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diff = Math.round((midnight(ship) - midnight(now)) / 86400000);
        set('[data-delivery-ship]', diff === 0 ? (el.dataset.today || 'Today') : diff === 1 ? (el.dataset.tomorrow || 'Tomorrow') : day(ship));
        set('[data-delivery-arrive]', range(addDays(start, min), addDays(start, max)));
        const c = $('[data-delivery-cutoff]', el);
        if (!c) return;
        // urgency timer: counts down from the set length to 0, then starts over
        // (tied to the clock, so it reads the same on every page and reload)
        const period = (parseFloat(el.dataset.timerHours) || 8) * 3600000;
        const mins = Math.max(1, Math.ceil((period - (Date.now() % period)) / 60000));
        set('[data-delivery-countdown]', `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`);
        c.hidden = false;
      };
      render();
      setInterval(render, 5000);
    });

    /* real stock level */
    const stock = $('[data-stock]', root);
    const updateStock = (v) => {
      if (!stock) return;
      const info = v && inventory[v.id];
      const threshold = parseInt(stock.dataset.threshold, 10) || 10;
      const show = info && info.tracked && info.qty > 0 && info.qty <= threshold;
      stock.hidden = !show;
      if (!show) return;
      $('[data-stock-text]', stock).textContent = stock.dataset.text.replace('[count]', info.qty);
      stock.style.setProperty('--stock', `${Math.max(8, Math.round((info.qty / threshold) * 100))}%`);
    };

    /* variants */
    const selected = () => fieldsets.map((fs) => $('input:checked', fs)?.value);
    const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const markAvailability = () => {
      const cur = selected();
      fieldsets.forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const test = cur.slice();
          test[idx] = input.value;
          const v = findVariant(test);
          input.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    };
    const initial = idInput ? variants.find((v) => String(v.id) === idInput.value) : findVariant(selected());
    updateStock(initial || variants[0]);

    const update = () => {
      const opts = selected();
      fieldsets.forEach((fs, i) => { const l = $('[data-option-label]', fs); if (l) l.textContent = opts[i]; });
      const v = findVariant(opts);
      markAvailability();
      updateStock(v);
      if (errorEl) errorEl.hidden = true;
      if (!v) {
        if (addBtn) { addBtn.disabled = true; addLabel.textContent = 'Unavailable'; }
        return;
      }
      if (idInput) {
        idInput.value = v.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (addBtn) { addBtn.disabled = !v.available; addLabel.textContent = v.available ? addText : 'Sold out'; }

      if (priceEl) {
        const now = $('[data-price-now]', priceEl);
        const was = $('[data-price-was]', priceEl);
        const badge = $('[data-price-badge]', priceEl);
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        now.textContent = formatMoney(v.price);
        was.textContent = sale ? formatMoney(v.compare_at_price) : '';
        was.hidden = !sale;
        badge.hidden = !sale;
        if (sale) badge.textContent = `Save ${Math.floor(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}%`;
      }
      if (v.featured_media) showMedia(v.featured_media.id);
      if (isProductPage) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      }
      if (sticky) {
        const sv = $('[data-sticky-variant]', sticky);
        const sp = $('[data-sticky-price]', sticky);
        if (sv) sv.textContent = v.title;
        if (sp) sp.textContent = formatMoney(v.price);
        const sb = $('[data-sticky-add]', sticky);
        if (sb) { sb.disabled = !v.available; sb.textContent = v.available ? 'Add to cart' : 'Sold out'; }
      }
    };
    fieldsets.forEach((fs) => fs.addEventListener('change', update));
    markAvailability();

    if (!form || !addBtn) return;

    /* quantity */
    const qtyInput = $('.qty__input', form);
    $$('[data-qty-step]', form).forEach((b) => b.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + parseInt(b.dataset.qtyStep, 10));
    }));

    /* add to cart */
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled || addBtn.classList.contains('is-loading')) return;
      addBtn.classList.add('is-loading');
      errorEl.hidden = true;
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      const request = fetch(`${S.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body
      }).then(async (res) => {
        const data = await res.json();
        if (!res.ok || data.status) throw Object.assign(new Error(data.description || data.message || 'Something went wrong. Please try again.'), { shown: true });
        return data;
      });

      // The upsell popup (main product page) opens on the tap, while the item is added in the background;
      // its buttons wait for that add to finish. The cart only opens once the items are in it (the Buy
      // button shows its spinner meanwhile), so shoppers never see it empty.
      Offer.pending = request;
      const extras = isProductPage ? Offer.appExtras(form.closest('.product-main') || root) : [];
      const offerShown = isProductPage && !extras.unsure && Offer.maybeShow([body.get('product-id'), ...extras]);
      const r = addBtn.getBoundingClientRect();
      poof(r.left + r.width / 2, r.top + r.height / 2, 16);

      try {
        const data = await request;
        Cart.render(data.sections);
        if (offerShown) Offer.recheck();
        else if (!(isProductPage && Offer.maybeShow()) && !document.body.classList.contains('template-cart')) Cart.open();
      } catch (err) {
        if (offerShown) Offer.abort();
        if (err.shown) {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        } else form.submit();   // network trouble: fall back to the normal form post
      } finally {
        if (Offer.pending === request) Offer.pending = null;
        addBtn.classList.remove('is-loading');
      }
    });

    /* sticky add-to-cart bar */
    if (sticky) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        const sb = $('[data-sticky-add]', sticky);
        if (sb) sb.tabIndex = show ? 0 : -1;
      }).observe(addBtn);
      $('[data-sticky-add]', sticky)?.addEventListener('click', () => (form.requestSubmit ? form.requestSubmit() : form.submit()));
    }
  }

  /* ---------- Upsell popup after Add to cart (snippets/upsell-offer.liquid) ----------
     Honest urgency: the timer's end time is stored for the visit, so reloading never resets it.
     When it runs out the offer closes and is not shown again; the discount code is only applied on accept. */
  const Offer = {
    pending: null,   // the add-to-cart request that opened the popup, while it's still running
    settled() { return Promise.resolve(this.pending).catch(() => {}); },
    // the add that opened the popup failed (e.g. sold out): take the popup away again
    abort() {
      $$('[data-offer]').forEach((d) => {
        if (!d.open) return;
        clearInterval(d._clock);
        clearTimeout(d._idle);
        d.close();
      });
    },
    key(id) { return `swello-offer-${id}`; },
    state(id) { try { return JSON.parse(sessionStorage.getItem(this.key(id)) || '{}'); } catch (e) { return {}; } },
    save(id, s) { try { sessionStorage.setItem(this.key(id), JSON.stringify(s)); } catch (e) { /* storage blocked */ } },

    // Countdown end for this popup: keeps the running one across pages, starts a fresh one once it ran out
    endTime(d) {
      const minutes = parseInt(d.dataset.minutes, 10) || 0;
      if (minutes <= 0) return 0;
      const st = this.state(d.dataset.offerId);
      return st.end > Date.now() ? st.end : Date.now() + minutes * 60000;
    },

    // Products in the cart, read from the drawer the server last rendered (no extra request).
    // Shipping protection is already left out there.
    cartProducts() {
      const inner = $('[data-cart-drawer-inner]');
      return (inner?.dataset.cartProducts || '').split(',').filter(Boolean);
    },

    // The first popup (in block order) whose cart rule matches
    pick(dialogs, productIds) {
      const inCart = new Set(productIds.map(String));
      return dialogs.find((d) => {
        if (inCart.has(d.dataset.productId)) return false;
        const skipIf = (d.dataset.exclude || '').split(',').map((x) => x.trim()).filter(Boolean);
        if (skipIf.some((id) => inCart.has(id))) return false;   // "Don't show if the cart has any of"
        const need = (d.dataset.require || '').split(',').map((x) => x.trim()).filter(Boolean);
        if (!need.every((id) => inCart.has(id))) return false;
        if (d.dataset.exact === 'true' && [...inCart].some((id) => !need.includes(id))) return false;
        return true;
      });
    },

    // Products an app adds together with the main one. Kaching Bundles: the selected deal's
    // ticked add-ons (e.g. "+2 Puff Cooling Pillow") and its free gifts, read from Kaching's own settings.
    // `ids.unsure` = the selected deal adds products we can't read in advance (e.g. a "Full Bed Set" bundle):
    // then the popup waits for the real cart instead of guessing.
    appExtras(root) {
      const ids = [];
      ids.unsure = false;
      try {
        const bar = $('.kaching-bundles__bar--selected', root);
        if (!bar) return ids;
        if ($('.kaching-bundles__bundle-products__product', bar)) { ids.unsure = true; return ids; }
        // Kaching keeps its deal settings in one or more <script> tags that may sit anywhere on the page
        // (newer versions put them in <head>, split per block): look in all of them for the selected deal.
        let deal = null;
        for (const cfg of $$('script.kaching-bundles-deal-block-settings')) {
          try { deal = (JSON.parse(cfg.textContent).dealBars || []).find((b) => b.id === bar.dataset.dealBarId); } catch (err) { /* skip unreadable */ }
          if (deal) break;
        }
        // Can't read the deal but an add-on is ticked: wait for the real cart instead of guessing
        if (!deal) { ids.unsure = !!$('.kaching-bundles__upsell__checkbox--selected', bar) || !!$('.kaching-bundles__upsell', bar); return ids; }
        const pid = (gid) => String(gid || '').split('/').pop();
        (deal.upsells || []).forEach((u) => {
          const box = bar.querySelector(`[data-upsell-id="${u.id}"] .kaching-bundles__upsell__checkbox`);
          const ticked = box ? box.classList.contains('kaching-bundles__upsell__checkbox--selected') : u.preselected;
          if (ticked && u.productGID) ids.push(pid(u.productGID));
        });
        (deal.freeGifts || []).forEach((g) => { if (g && g.productGID) ids.push(pid(g.productGID)); });
      } catch (e) { ids.unsure = true; /* unknown app markup: wait for the real cart */ }
      return ids;
    },

    // Shows the matching popup right away. `adding` = product ids being added right now (counted as in the cart).
    maybeShow(adding = []) {
      const dialogs = $$('[data-offer]').filter((d) => d.showModal);
      if (!dialogs.length) return false;
      const ids = this.cartProducts().concat([].concat(adding).filter(Boolean).map(String));
      const d = this.pick(dialogs, ids);
      if (!d) return false;
      const id = d.dataset.offerId;
      const end = this.endTime(d);
      this.save(id, { end });
      this.bind(d, id, end);
      this.loadPhotos(d);
      d.showModal();
      d.focus({ preventScroll: true });   // focus the popup itself, not its first button (no "selected" ring on the arrow or X)
      d._showVariantPhoto?.();   // start on the selected color's photo, with the right prices
      this.idle(d);
      return true;
    },

    // Popup photos wait (loading="lazy") so hidden popups cost nothing on page load; fetch them as it opens
    loadPhotos(d) {
      $$('img[loading="lazy"]', d).forEach((img) => { img.loading = 'eager'; });
    },

    // After the add, compare with the real cart (apps can add more than we predicted):
    // if a different popup should be showing, swap to it; if none should, open the cart instead.
    recheck() {
      const open = $$('[data-offer]').find((d) => d.open);
      if (!open) return;
      const dialogs = $$('[data-offer]').filter((d) => d.showModal);
      if (this.pick(dialogs, this.cartProducts()) === open) return;
      clearInterval(open._clock);
      clearTimeout(open._idle);
      open.close();
      if (!this.maybeShow() && !document.body.classList.contains('template-cart')) Cart.open();
    },

    // No interaction for the set number of seconds → checkout (0 = off)
    idle(d) {
      clearTimeout(d._idle);
      const secs = parseInt(d.dataset.idle, 10) || 0;
      if (secs > 0 && d.open) d._idle = setTimeout(() => { if (d.open) d._finish('idle'); }, secs * 1000);
    },

    bind(d, id, end) {
      if (d._bound) { this.startClock(d, id, end); return; }
      d._bound = true;
      const qty = parseInt(d.dataset.qty, 10) || 1;
      const discount = parseInt(d.dataset.discount, 10) || 0;
      const select = $('[data-offer-variant]', d);
      const accept = $('[data-offer-accept]', d);

      // photo gallery: swipe, arrows, dots; a color choice jumps to that color's photo
      const track = $('[data-offer-track]', d);
      const slides = track ? $$('.offer__slide', track) : [];
      const dots = $$('.offer__dot', d);
      let cur = 0;
      const goTo = (i, smooth = true) => {
        if (!track || !slides[i]) return;
        cur = i;
        track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'instant' });
        dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
      };
      track?.addEventListener('scroll', () => {
        const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (i !== cur) { cur = i; dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i)); }
      }, { passive: true });
      $('[data-offer-prev]', d)?.addEventListener('click', () => goTo((cur - 1 + slides.length) % slides.length));
      $('[data-offer-next]', d)?.addEventListener('click', () => goTo((cur + 1) % slides.length));

      const renderPrice = (smooth = true) => {
        const opt = select && select.selectedOptions[0];
        const unit = opt ? parseInt(opt.dataset.price, 10) : null;
        if (unit == null || Number.isNaN(unit)) return;
        const was = unit * qty;
        const off = Math.floor((was * discount) / 100);
        $('[data-offer-now]', d).textContent = formatMoney(was - off);
        $('[data-offer-was]', d).textContent = formatMoney(was);
        $('[data-offer-save]', d).textContent = `You save ${formatMoney(off)}`;
        $('.btn__label', accept).textContent = (accept.dataset.label || '').replace('[save]', formatMoney(off));
        const idx = slides.findIndex((s) => s.dataset.mediaId === opt.dataset.media);
        if (idx >= 0) goTo(idx, smooth);
      };
      select?.addEventListener('change', () => renderPrice(true));
      d._showVariantPhoto = () => renderPrice(false);

      // how: 'accepted' | 'closed' (X, Esc, backdrop) | 'declined' (decline link) | 'idle' (no tap for the set time)
      const finish = async (how) => {
        clearInterval(d._clock);
        clearTimeout(d._idle);
        if (d.open) d.close();
        // declined or left idle: straight to checkout (when set), otherwise the cart.
        // The product that opened the popup may still be on its way into the cart: wait for it first.
        if ((how === 'declined' && d.dataset.declineTo === 'checkout') || how === 'idle') {
          await this.settled();
          window.location.href = `${S.routes.root.replace(/\/$/, '')}/checkout`;
          return;
        }
        // accepted: the next popup in line may now match (e.g. comforter + pillows → eye mask)
        if (how === 'accepted' && this.maybeShow()) return;
        if (!document.body.classList.contains('template-cart')) Cart.open();
      };
      d._finish = finish;
      const canClose = !!$('[data-offer-close]', d);
      d.addEventListener('cancel', (e) => { e.preventDefault(); if (canClose) finish('closed'); });   // Esc key
      d.addEventListener('click', (e) => {
        if (e.target.closest('[data-offer-decline]')) finish('declined');
        else if (canClose && (e.target === d || e.target.closest('[data-offer-close]'))) finish('closed');
      });
      // any tap, key or scroll restarts the idle countdown
      ['pointerdown', 'keydown', 'input', 'scroll', 'touchstart'].forEach((type) => d.addEventListener(type, () => this.idle(d), { passive: true, capture: true }));
      accept.addEventListener('click', async () => {
        if (accept.classList.contains('is-loading')) return;
        accept.classList.add('is-loading');
        const variantId = Number(select ? select.value : d.dataset.variantId);
        try {
          const code = d.dataset.code;
          await this.settled();   // the main product goes in first, so the cart keeps the order shoppers expect
          await Cart.add([{ id: variantId, quantity: qty }]);
          // the offer's code goes on the cart itself, so the drawer shows the discounted price straight away;
          // if that fails, fall back to the checkout-only discount link so the price is still right at checkout
          if (code) {
            await Cart.applyCode(code).catch(() =>
              fetch(`${S.routes.root.replace(/\/$/, '')}/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {}));
          }
          await finish('accepted');
        } catch (err) {
          alert(err.message || 'Could not add this item.');
        } finally {
          accept.classList.remove('is-loading');   // ready again if the popup re-opens later
        }
      });
      this.startClock(d, id, end);
    },

    startClock(d, id, end) {
      const clock = $('[data-offer-clock]', d);
      clearInterval(d._clock);
      if (!end || !clock) return;
      // counts down, and when it reaches 0 it simply starts over: the offer never disappears
      const tick = () => {
        if (end - Date.now() <= 0) {
          end = this.endTime(d);
          this.save(id, { end });
        }
        const left = Math.max(0, end - Date.now());
        const m = Math.floor(left / 60000);
        const s = Math.floor((left % 60000) / 1000);
        clock.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      };
      tick();
      d._clock = setInterval(tick, 1000);
    }
  };

  /* ---------- Swipeable product row: progress bar under it ---------- */
  function SwipeRow(row) {
    const bar = row.parentElement.querySelector('[data-swipe-bar]');
    if (!bar) return;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      bar.parentElement.classList.toggle('is-hidden', max <= 2);
      if (max <= 2) return;
      const size = row.clientWidth / row.scrollWidth;
      const progress = clamp(row.scrollLeft / max);
      bar.style.setProperty('--size', `${(size * 100).toFixed(2)}%`);
      bar.style.setProperty('--pos', `${(progress * (1 / size - 1) * 100).toFixed(2)}%`);
    };
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- Videos: play muted while visible, tap for sound ---------- */
  function Videos(scope) {
    const vids = $$('.vcard__video, .mrow__video', scope);
    if (!vids.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting && !reduce) target.play?.().catch(() => {});
        else target.pause?.();
      });
    }, { threshold: 0.25 });
    vids.forEach((v) => { v.muted = true; v.setAttribute('playsinline', ''); io.observe(v); });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sound]');
    if (!btn) return;
    const video = $('video', btn.closest('.vcard'));
    if (!video) return;
    const turnOn = video.muted;
    $$('.vcard video').forEach((v) => { v.muted = true; });
    $$('[data-sound]').forEach((b) => { b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', 'Turn sound on'); });
    if (turnOn) {
      video.muted = false;
      video.play?.().catch(() => {});
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', 'Turn sound off');
    }
  });

  /* ---------- Accordions with smooth height ---------- */
  function Accordion(details) {
    const summary = $('summary', details);
    const body = $('.acc__body', details);
    if (!summary || !body || reduce) return;
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (details.dataset.animating) return;
      details.dataset.animating = '1';
      const done = () => { delete details.dataset.animating; };
      if (!details.open) {
        details.open = true;
        const h = body.scrollHeight;
        body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 500, easing: 'cubic-bezier(.16,1,.3,1)' }).onfinish = done;
      } else {
        const h = body.scrollHeight;
        body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 350, easing: 'ease' }).onfinish = () => { details.open = false; done(); };
      }
    });
  }

  /* ---------- Boot ---------- */
  const once = new WeakSet();
  const components = [
    ['[data-hero]', Hero],
    ['[data-announce]', Announcement],
    ['[data-countdown-bar]', CountdownBar],
    ['[data-marquee]', Marquee],
    ['[data-stuffed]', Stuffed],
    ['[data-hscroll]', HScroll],
    ['[data-squish]', Squish],
    ['[data-night]', Night],
    ['[data-buy-box]', BuyBox],
    ['details.acc', Accordion],
    ['.vreviews, .mrows', Videos],
    ['[data-swipe-row]', SwipeRow]
  ];

  function init(scope = document) {
    $$('[data-split]', scope).forEach(splitText);
    $$('[data-reveal], [data-split], [data-stat]', scope).forEach((el) => {
      if (el.closest('[data-hero]')) return;
      if (reduce) el.classList.add('is-in');
      else revealIO.observe(el);
    });
    components.forEach(([sel, fn]) => {
      $$(sel, scope).forEach((el) => {
        if (once.has(el)) return;
        once.add(el);
        try { fn(el); } catch (err) { console.error('[Swello]', sel, err); }
      });
    });
  }

  init();
  Header();
  Cart.init();
  Loader();
  requestAnimationFrame(Ticker.loop);

  /* Upsell popup preview, never on the live theme: open a popup without adding to the cart,
     to check its look and test Accept. Theme editor: select an "Upsell popup" block.
     Unpublished/development preview: add ?offer=2 to a product page to open its 2nd popup. */
  const previewOffer = (d) => {
    if (!d || !d.showModal) return;
    Offer.bind(d, d.dataset.offerId, Offer.endTime(d));
    Offer.loadPhotos(d);
    if (!d.open) d.showModal();
    d._showVariantPhoto?.();
  };
  document.addEventListener('shopify:block:select', (e) => { if (e.target.matches?.('[data-offer]')) previewOffer(e.target); });
  document.addEventListener('shopify:block:deselect', (e) => { if (e.target.matches?.('[data-offer]') && e.target.open) e.target.close(); });
  const offerParam = new URLSearchParams(window.location.search).get('offer');
  if (offerParam && window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main') previewOffer($$('[data-offer]')[Number(offerParam) - 1]);

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.hscroll');
    if (slide) e.target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    // Upsell popup block: show it while it's being edited (no timer, no cart check)
    if (e.target.matches('[data-offer]') && !e.target.open) e.target.showModal();
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    if (e.target.matches('[data-offer]') && e.target.open) e.target.close();
  });

  window.Swello = Object.assign(S, { Cart, poof, formatMoney });
})();
